import type { CSSProperties } from 'react';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

export const SW_UPDATE_TOAST_ID = 'sw-update';

const UPDATE_TOAST_LABEL =
  'A new version of this application is available. Please save your work and refresh.';

interface SWUpdateToastProps {
  onClick: () => void;
}

/**
 * The single in-app "new version available" notification, surfaced as a filled
 * info banner (DESIGN.md info family) so it reads as a system update. Tapping
 * anywhere activates the waiting service worker, which reloads the page once on
 * `controllerchange`. This is the only update prompt — the old `/version.json`
 * StaleBanner was removed so users no longer get two colliding notifications.
 */
export default function SWUpdateToast({ onClick }: SWUpdateToastProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const bannerStyle: CSSProperties = {
    alignItems: 'center',
    background: theme.palette.info.main,
    border: 'none',
    borderRadius: 8,
    boxShadow: isDark
      ? '0 12px 28px rgba(0, 0, 0, 0.48)'
      : '0 12px 28px rgba(30, 20, 10, 0.18)',
    boxSizing: 'border-box',
    color: theme.palette.info.contrastText,
    cursor: 'pointer',
    display: 'flex',
    font: 'inherit',
    gap: 12,
    lineHeight: 1.4,
    maxWidth: 'calc(100vw - 32px)',
    padding: '12px 16px',
    textAlign: 'left',
    width: 'min(560px, calc(100vw - 32px))',
  };

  const labelStyle: CSSProperties = {
    flex: 1,
    fontSize: '0.95rem',
    fontWeight: 600,
    letterSpacing: 0,
  };

  const actionStyle: CSSProperties = {
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.18)',
    borderRadius: 6,
    display: 'inline-flex',
    flexShrink: 0,
    gap: 4,
    fontSize: '0.85rem',
    fontWeight: 700,
    padding: '6px 10px',
    whiteSpace: 'nowrap',
  };

  return (
    <button
      type="button"
      aria-label={UPDATE_TOAST_LABEL}
      data-toast-id={SW_UPDATE_TOAST_ID}
      onClick={onClick}
      style={bannerStyle}
    >
      <InfoOutlinedIcon fontSize="small" aria-hidden style={{ flexShrink: 0 }} />
      <span style={labelStyle}>{UPDATE_TOAST_LABEL}</span>
      <span style={actionStyle}>
        <RefreshIcon fontSize="small" aria-hidden />
        Refresh Now
      </span>
    </button>
  );
}
